import {
  ACCOUNT_TIERS,
  AccountInfoResponse,
  type AccountTier,
} from 'types/billing';
import { BillingDateRange } from '../components/Billing/types';
import {
  GRANULARITY,
  Granularity,
} from '../network/httpClients/BillingClient/api.schema';

// Number of characters to show for abbreviated month names (e.g., "Jan", "Feb")
const MONTH_ABBREVIATION_LENGTH = 3;

/**
 * Format a chart label based on granularity.
 * @param value - Raw label string from the data point (e.g. "01 Jan 2026").
 * @param granularity - Granularity string controlling the output format.
 * @returns Formatted label for chart display.
 */
export const formatChartLabel = (
  value: string,
  granularity: string,
): string => {
  if (granularity === GRANULARITY.DAY) {
    const [day, mon] = value.split(' ');

    return `${mon} ${parseInt(day)}`;
  }

  if (granularity === GRANULARITY.MONTH) {
    return value.split(' ')[0];
  }

  return value;
};

/**
 * Format X-axis label for chart display.
 * @param value - Full label string for the bucket.
 * @param granularity - Granularity of the chart buckets.
 * @returns Abbreviated label suitable for the X axis.
 */
export const formatXAxisLabel = (
  value: string,
  granularity: Granularity,
): string => {
  if (granularity === GRANULARITY.DAY) {
    return value;
  }

  return value.slice(0, MONTH_ABBREVIATION_LENGTH);
};

/**
 * Convert a {from, to} date range to ISO strings for the API.
 *
 * The picker hands us calendar-day Dates at local midnight. We send the UTC
 * midnight of those calendar days — toISOString-ing the local Date directly
 * shifts the date by the user's TZ offset and pulls in extra months on the
 * server's bucket grid. End is exclusive on the API, so we bump the picked
 * end day forward by one calendar day so the user's last day is included.
 * @param range - Billing date range with `from` and `to` calendar-day Dates.
 * @returns Object with `startDate` and `endDate` as UTC ISO strings.
 */
export const rangeToIsoParams = (
  range: BillingDateRange,
): { startDate: string; endDate: string } => {
  const startUtc = new Date(
    Date.UTC(
      range.from.getFullYear(),
      range.from.getMonth(),
      range.from.getDate(),
    ),
  );
  const endUtc = new Date(
    Date.UTC(
      range.to.getFullYear(),
      range.to.getMonth(),
      range.to.getDate() + 1,
    ),
  );

  return {
    startDate: startUtc.toISOString(),
    endDate: endUtc.toISOString(),
  };
};

/**
 * Returns true when the tier represents a self-serve paid subscription.
 * @param tier - Tier string from /account-info, or undefined while loading.
 * @returns `true` for the `paid` tier, `false` for everything else.
 */
export const isPaidTier = (
  tier: AccountTier | string | null | undefined,
): boolean => tier === ACCOUNT_TIERS.paid;

/**
 * Determine whether an account is currently in a trial.
 *
 * Keys off the `trial.active` flag from /account-info rather than the mere
 * presence of a `trial` object, since the response carries trial metadata even
 * once the trial has lapsed.
 *
 * A converted (paid) account is never treated as in-trial, even though
 * /account-info may still report `trial.active=true` after conversion
 * (trial_end_date isn't cleared on conversion: PLT-4156).
 * @param accountInfo - The account-info response, or undefined while loading.
 * @returns `true` when the account is on a (non-paid) active trial, otherwise `false`.
 */
export const isAccountInTrial = (
  accountInfo: AccountInfoResponse | undefined,
): boolean => {
  if (isPaidTier(accountInfo?.tier)) {
    return false;
  }

  return !!accountInfo?.trial?.active;
};

/**
 * Classify review complexity based on credits consumed.
 * @param credits - Number of credits the review consumed.
 * @returns Human-readable complexity label: `"Light"`, `"Standard"`, or `"Complex"`.
 */
export const getComplexityLabel = (credits: number): string => {
  if (credits < 130) return 'Light';

  if (credits <= 190) return 'Standard';

  return 'Complex';
};
